<template>
	<div class="class-page">
		<ClassPageHeader :details="details"
										 :current-section="parseInt(sectionId)"
										 :course-content="courseContent"
										 :course="course"
										 :menu="menu"
										 :is-class-item-page="true" />
		<div class="back-link">
			<a style="cursor:pointer" @click="goToClassPage()">
				<i class="fas fa-caret-left" aria-hidden="true"></i>{{ $t('MAIN_CONTENT.commonContent.goBack') }}
			</a>
		</div>
		<div class="glossary" v-if="!isLoading">
			<glossary-index :terms="termList" @setFilterByValues="setFilterBy"></glossary-index>
			<div class="glossary-body">
				<button class="btn btn-primary new-btn"
								@click="openForm" :disabled="showForm"
								v-if="glossary.ALLOW_STUDENT_ENTRY_FLG === 'Y' && role === 'student'">
					<i class="fas fa-plus"></i>
					{{ $t('MAIN_CONTENT.commonContent.newEntryTitleCase') }}
				</button>
				<glossary-search :total="glossary.ENTRIES.length"
												:searchQuery="searchText"
												@updateSearch="updateSearchText"
												@showAll="showAllEntries"
												@hideAll="hideAllEntries"
												v-if="glossary.ENTRIES && glossary.ENTRIES.length">
				</glossary-search>
				<glossary-form v-if="showForm" @closeForm="closeFormReload" :type="glossaryType()"></glossary-form>
				<glossary-results :entries="filteredGlossary"
													:type="glossaryType()"
													:canEdit="glossary.ALLOW_STUDENT_ENTRY_FLG === 'Y'"
													@clearResults="clearResults"
													@clearTerm="clearTerm">
				</glossary-results>
			</div>
		</div>
		<div class="back-link">
			<a style="cursor:pointer" @click="goToClassPage()">
				<i class="fas fa-caret-left" aria-hidden="true"></i>{{ $t('MAIN_CONTENT.commonContent.goBack') }}
			</a>
		</div>
	</div>
</template>

<script>
	import { mapGetters, mapActions } from 'vuex';
	import { Tabs, Tab } from 'vue-tabs-component';

	import ClassPageHomeResource from '../../api/ClassPageHome';
	import ALPHABET from './glossary/constants.js';
	import GlossaryIndex from './glossary/GlossaryIndex.vue';
	import GlossaryResults from './glossary/GlossaryResults.vue';
	import GlossarySearch from './glossary/GlossarySearch.vue';
	import GlossaryForm from './glossary/GlossaryForm.vue';
	import Modal from 'COMMONS/shared/Modal/Modal.vue';

	import ClassPageHeader from "./Header.vue";
	import ClassPageFooter from "./Footer.vue";

	import ClassPageDetailsResource from '../../api/ClassPageDetails';
	import ClassPageMenuResource from '../../api/ClassPageMenu';
	import CoursesResource from '../../api/Courses';

	// API imports
	import Glossary from '../../api/Glossary.js';

	/**
		* @vue-prop {String} courseId - The course ID.
		* @vue-prop {Number} id - The glossary id.
		* @vue-prop {String} [local=en] - Albphabet's language ( currently en - English or es - Spanish ).
		* @vue-prop {Number} [section=0] - The class page section.
		* @vue-computed {Object} glossary - The glossary.
		* @vue-computed {Array} termList - The list of terms ( array of strings ).
	*/
	export default {
		data() {
			return {
				...ALPHABET,
				glossary: {},
				filteredGlossary: [],
				isLoading: true,
				showForm: false,
				showDrivePicker: false,
				searchText: '',
				menu: [],
				details: {},
				course: {},
				courseContent: {},
			};
		},
		computed: {
			...mapGetters(['selectedStudent', 'roleRules', 'locale', 'role']),
			termList() {
				let list = {};
				let locale = 'en-us';

				if( !!this.locale ) {
					if( Object.keys( this.locale ).length > 0 ) {
						locale = this.locale.LOCALE_CODE;
					}
				}

				this.ALPHABET[ locale ].forEach(letter => {
					list[letter] = [];
				});
				
				if(!!this.glossary) {
					if(!!this.glossary.ENTRIES) {
						this.glossary.ENTRIES.forEach(entry => {
							try {
								if(!!list[entry.LETTER.toUpperCase()]) {
									list[entry.LETTER.toUpperCase()].push(entry);
								} else {
									list[entry.LETTER.toUpperCase()] = [];
									list[entry.LETTER.toUpperCase()].push(entry);
								}
							} catch(error) {
								if(!!list[entry.LETTER]) {
									list[entry.LETTER].push(entry);
								} else {
									list[entry.LETTER] = [];
									list[entry.LETTER].push(entry);
								}
							}
						});
					}
				}

				return list;
			},
			sectionId() {
				return this.$route.params.folderId ? this.$route.params.folderId : 0;
			},
		},
		components: {
			GlossaryIndex,
			GlossaryResults,
			GlossarySearch,
			GlossaryForm,
			Modal,
			Tabs,
			Tab,
			ClassPageHeader,
			ClassPageFooter
		},
		watch: {
			searchText() {
				let value = [];
				let text = this.searchText;

				if(!!this.searchText) {
					if(typeof text !== 'string') {
						text = text.toString();
					}

					text = text.toLowerCase();

					for(let g in this.glossary.ENTRIES) {
						let entryValaue = this.glossary.ENTRIES[g].TERM;

						entryValaue = entryValaue.toLowerCase();

						if(entryValaue.startsWith(text)) {
							value.push(this.glossary.ENTRIES[g]);
						}
					}
				}

				this.$set(this, 'filteredGlossary', value);
			}
		},
		methods: {
			...mapActions(['fetchToken']),
			glossaryType() {
				let type = this.$route.params.glossaryType;
				let route = 'glossaries';

				if(type === 'C' || type === 'P') {
					route = 'glossaries'
				} else if(type === 'G') {
					route = 'student-group-glossaries'
				} else if(type === 'S') {
					route = 'student-glossaries';
				}

				return route;
			},
			updateSearchText(text) {
				this.$set(this, 'searchText', text);
			},
			fetchGlossary() {
				const self = this;

				const glossary = new Glossary({
					routeParams: {
						courseSectionGuid: self.$route.params.courseSectionGUID,
						glossaryID: self.$route.params.glossaryID,
						glossaryType: self.glossaryType()
					},
					query: {
						student_rid: self.selectedStudent.STUDENT_RID,
					}
				});

				glossary.get().then(response => {
					let gData = glossary.getResponseData();

					for(let e in gData.ENTRIES) {
						let entry = gData.ENTRIES[e];

						self.$set(entry, 'NEW_FILES', []);
					}

					self.$set(self, 'glossary', gData);
					self.isLoading = false;
				});
			},
			showAllEntries() {
				this.searchText = '';
				this.filteredGlossary = this.glossary.ENTRIES;
			},
			hideAllEntries() {
				this.searchText = '';
				this.filteredGlossary = [];
			},
			openForm() {
				this.filteredGlossary = [];
				this.searchText = '';
				this.fetchToken();
				this.showForm = true;
			},
			closeDrivePicker() {
				this.showDrivePicker = false;
			},
			setFilterBy(val) {
				this.$set(this, 'filteredGlossary', val);
			},
			closeFormReload() {
				this.showForm = false;
				this.fetchGlossary();
			},
			clearResults() {
				this.filteredGlossary = [];
				this.searchText = '';
				this.fetchGlossary();
			},
			clearTerm(term) {
				this.searchText = '';

				for(let f in this.filteredGlossary) {
					let filtered = this.filteredGlossary[f];

					if(filtered.GLOSSARY_ENTRY_ID === term.GLOSSARY_ENTRY_ID) {
						this.filteredGlossary.splice(f, 1);
						break;
					}
				}

				this.fetchGlossary();
			},
			fetchMenu() {
				const ClassPageMenu = new ClassPageMenuResource({
					routeParams: {
						courseSectionGuid: this.$route.params.courseSectionGUID,
					},
					query: {
						student_rid: this.selectedStudent.STUDENT_RID,
					},
				});

				ClassPageMenu.get().then(()=>{
					this.menu = ClassPageMenu.getResponseData();
				});
			},
			fetchDetails() {
				const ClassPageDetails = new ClassPageDetailsResource({
					routeParams: {
						courseSectionGuid: this.$route.params.courseSectionGUID,
					},
					query: {
						student_rid: this.selectedStudent.STUDENT_RID,
					},
				});

				ClassPageDetails.get().then(()=>{
					this.details = ClassPageDetails.getResponseData();
				});
			},
			fetchCourse() {
				const Course = new CoursesResource({
					routeParams: {
						courseSectionGuid: this.$route.params.courseSectionGUID,
					},
					query: {
						student_rid: this.selectedStudent.STUDENT_RID,
					},
				});

				Course.get().then(()=>{
					this.course = Course.getResponseData();
				});
			},
			goToClassPage() {
				let params = this.$route.params;
				
				this.$router.push({
					name: 'ClassPageHome',
					params: {
						courseSectionGUID: this.$route.params.courseSectionGUID
					},
				});
			},
			fetchClassHome(){
				const ClassPageHome = new ClassPageHomeResource({
					routeParams: {
						courseSectionGuid: this.$route.params.courseSectionGUID,
					},
					query: {
						student_rid: this.selectedStudent.STUDENT_RID,
					},
				});

				ClassPageHome.get().then(()=>{
					this.courseContent = ClassPageHome.getResponseData().COURSE_CONTENT;
				});
			},
		},
		created() {
			let header = '';

			switch(this.glossaryType()) {
				case 'glossaries':
					header = this.$t('MAIN_CONTENT.ClassGlossary.glossary');
					break;
				case 'student-group-glossaries':
					header = this.$t('MAIN_CONTENT.ClassGlossary.studentGroupGlossaryTitleCase');
					break;
				case 'student-glossaries':
					header = this.$t('MAIN_CONTENT.ClassGlossary.studentClassGlossaryTitleCase');
					break;
			}

			this.$store.commit('setTitleIcon', 'spell-check');
			this.$store.commit('setTitle', header);
			this.$store.commit('setUseHeader', true);
			
			this.fetchMenu();
			this.fetchDetails();
			this.fetchCourse();
			this.fetchClassHome();

			this.fetchGlossary();
		}
	};
</script>
